// Melding som «case»: toveis dialog + status rundt én stridsmelding (CRM/OODA).
// Kvittér raskt, svar i tråd, sett status — alt for å korte ned tiden fra
// observasjon til handling. Gjenbrukes i hovedvindu og i C2-pop-out.
import {
  REPORT_FIELDS, STATUS_LABEL, STATUS_COLOR, REPORT_COLOR,
  reportSummary, buildDtg, type CombatReport, type ReportStatus,
} from '@skade/contracts';
import { KONTEKST_ETIKETT } from '../kontekst.js';

const STATUSES: ReportStatus[] = ['ny', 'kvittert', 'aktiv', 'løst'];

export interface ThreadCallbacks {
  onStatus: (s: ReportStatus) => void;
  onComment: (text: string) => void;
  onClose?: () => void;
  onGoto?: () => void;
}

export class ReportThreadView {
  readonly el: HTMLElement;
  private report: CombatReport;

  constructor(report: CombatReport, private cb: ThreadCallbacks) {
    this.report = report;
    this.el = div('thread-view');
    this.render();
  }

  update(r: CombatReport) {
    this.report = r;
    this.render();
  }

  private render() {
    const r = this.report;
    this.el.innerHTML = '';

    // Header: type + sammendrag + lukk.
    const head = div('thread-head');
    const t = span('thread-type', r.type);
    t.style.color = REPORT_COLOR[r.type] ?? '#5b9bd5';
    head.append(t, span('thread-summary', reportSummary(r)));
    if (this.cb.onGoto) {
      const goto = btn('⌖', () => this.cb.onGoto?.()); goto.title = 'Vis på kartet'; goto.className = 'thread-icon-btn';
      head.append(goto);
    }
    if (this.cb.onClose) {
      const x = btn('×', () => this.cb.onClose?.()); x.className = 'thread-icon-btn'; head.append(x);
    }
    this.el.append(head);

    // ── HVA MELDINGEN HANDLER OM (UX-funn F4) ──────────────────────────────
    // ⚠ MÅ STÅ I TRÅDEN, ikke bare på objektet. Uten denne linja er en forankret
    // melding umulig å skille fra en løsrevet: du ser samtalen, men ikke hva
    // dere snakker om — som er nettopp den «manuelle kryssjekken» funnet
    // beskriver. Etiketten er sitatet fra da meldingen ble skrevet, så den er
    // lesbar også etter at sporet er sluppet og ikke kan slås opp.
    if (r.omObjekt) {
      const anker = div('thread-anker');
      anker.append(span('thread-anker-merke', `OM ${r.omObjekt.slag.toUpperCase()}`));
      anker.append(span('thread-anker-navn', r.omObjekt.etikett || r.omObjekt.id));
      this.el.append(anker);
    }

    // Status-chips (ett trykk = kvittér/eskaler/lukk → driver OODA).
    const chips = div('thread-status');
    for (const s of STATUSES) {
      const c = btn(STATUS_LABEL[s], () => this.cb.onStatus(s));
      c.className = 'status-chip' + (s === r.status ? ' active' : '');
      if (s === r.status) { c.style.background = STATUS_COLOR[s]; c.style.borderColor = STATUS_COLOR[s]; }
      chips.append(c);
    }
    this.el.append(chips);

    // Felt (definert rekkefølge; textContent = ingen XSS).
    const fields = div('thread-fields');
    // Stridsmelding: vis DTG (auto fra tidspunkt, ALFA = norsk normaltid) øverst.
    if (r.type === 'MELDING') {
      const row = div('thread-kv');
      row.append(span('thread-k', 'DTG'), span('thread-v', buildDtg(r.tsMs, 'A')));
      fields.append(row);
    }
    // Kjente felt først, i doktrinær rekkefølge.
    const vist = new Set<string>();
    for (const f of REPORT_FIELDS[r.type] ?? []) {
      vist.add(f.key);
      const v = r.fields[f.key];
      if (!v) continue;
      const row = div('thread-kv');
      row.append(span('thread-k', f.label), span('thread-v', v));
      fields.append(row);
    }
    // DERETTER alle GJENVÆRENDE felt. Uten dette forsvant felt STILLE når GNÅ
    // og COP brukte ulike nøkler: en MEDEVAC fra GNÅ har 11 nøkler (9-linjers),
    // COP kjente 5, og bare 2 overlappet — så HLS-merking, båre-/gående-antall,
    // nasjonalitet og CBRN ble borte uten spor. TIC og SITREP viste 0 felt.
    // Å vise ukjente nøkler med utledet etikett er stygt i verste fall; å skjule
    // pasientdata er farlig. Derfor: aldri forkast et felt.
    for (const [k, v] of Object.entries(r.fields)) {
      if (vist.has(k) || !v || META_FELT.has(k)) continue;
      const row = div('thread-kv');
      row.classList.add('thread-kv-ukjent');
      row.append(span('thread-k', feltEtikett(k)), span('thread-v', String(v)));
      fields.append(row);
    }
    const meta = div('thread-meta');
    meta.textContent = `${new Date(r.tsMs).toISOString().slice(11, 19)}Z · ${shortId(r.author)}`;
    fields.append(meta);
    this.el.append(fields);

    // Dialogtråd.
    const thread = div('thread-list');
    if (!r.thread.length) thread.append(span('thread-empty', 'Ingen svar ennå — kvittér eller svar.'));
    for (const c of r.thread) {
      const item = div('thread-item');
      const by = span('thread-by', shortId(c.author));
      const when = span('thread-when', new Date(c.tsMs).toISOString().slice(11, 16) + 'Z');
      const txt = div('thread-text'); txt.textContent = c.text;
      const line = div('thread-item-head'); line.append(by, when);
      item.append(line, txt);
      thread.append(item);
    }
    this.el.append(thread);

    // Svarfelt.
    const replyBox = div('thread-reply');
    const input = document.createElement('textarea');
    input.className = 'thread-reply-input';
    input.placeholder = 'Svar i tråden …';
    input.rows = 2;
    const send = btn('Svar', () => {
      const text = input.value.trim();
      if (!text) return;
      this.cb.onComment(text);
      input.value = '';
    });
    send.className = 'thread-reply-send';
    // Enter sender, Shift+Enter = ny linje.
    input.onkeydown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send.click(); }
    };
    replyBox.append(input, send);
    this.el.append(replyBox);
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function btn(text: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button'); b.type = 'button'; b.textContent = text; b.onclick = onClick; return b;
}
function shortId(id: string): string { return id.replace(/^@/, '').replace(/:.*$/, ''); }


/** Meta-felt som vises andre steder (tid, posisjon) — ikke som nøkkel/verdi-rad. */
const META_FELT = new Set([
  'created_at', 'observed_at', 'sone', 'lat', 'lon', 'mgrs', 'acc', 'contact',
]);

/** Utled en lesbar etikett fra en ukjent feltnøkkel («prio_count» → «Prio count»).
 * Kontekst-feltene (avsender/enhet/dtg/posisjon) har faste etiketter fra ÉN kilde
 * (`kontekst.ts`) — ellers ville tråden vist «Dtg» der skjemaet sa «DTG». */
function feltEtikett(key: string): string {
  const fast = KONTEKST_ETIKETT[key];
  if (fast) return fast;
  const t = key.replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}
